import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { Lightbulb, Send } from 'lucide-react';
import { useChallenge, useSubmitChallenge } from '../../features/cases/hooks/use-challenges';
import { draftStore } from '../../hooks/useDraftStore';

const CaseResponsePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const draftKey = `challenge_response_${id}`;
  const textareaRef = useRef(null);

  const [response, setResponse] = useState(() => {
    return draftStore.loadDraft(draftKey) || '';
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { data, isLoading } = useChallenge(id);
  const submitMutation = useSubmitChallenge();

  // Auto-grow textarea height as content expands
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.max(220, scrollHeight)}px`;
    }
  }, [response]);

  // Auto-save typed response to draftStore
  useEffect(() => {
    if (response) {
      const timer = setTimeout(() => {
        draftStore.saveDraft(draftKey, response);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [response, draftKey]);

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex min-h-screen justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
        </div>
      </AppShell>
    );
  }

  const ch = data?.data;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const result = await submitMutation.mutateAsync({ id, response });
      draftStore.clearDraft(draftKey);
      // Store the response and attempt number in sessionStorage for the comparison page
      sessionStorage.setItem(`challenge_response_${id}`, response);
      sessionStorage.setItem(`challenge_attempt_${id}`, result?.data?.attemptNumber || 1);
      navigate(`/challenges/${id}/approach`);
    } catch (err) {
      console.error('Submission failed', err);
      setSubmitError(err?.response?.data?.message || 'An error occurred during submission.');
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto py-6 px-4">
          <button
            onClick={() => navigate(`/challenges/${id}/intake`)}
            className="text-gray-500 hover:text-gray-900 flex items-center text-sm font-medium mb-3 transition-colors"
          >
            <span className="mr-1">{"<"}</span> Back to Case Details
          </button>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">What would you do next?</h1>
          <p className="text-gray-500 text-sm">Share your diagnostic approach</p>
        </div>
      </div>

      <div className="bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20 space-y-6">
          {/* Case Info Banner */}
          {ch && (
            <div className="bg-blue-50/30 rounded-xl border border-blue-100 p-5">
              <h2 className="text-lg font-semibold text-[#1e3a8a] mb-1">{ch.title}</h2>
              <p className="text-blue-600/80 text-xs font-medium">{ch.species}</p>
            </div>
          )}

          {/* Response Form */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Your Clinical Approach</h3>

            <div className="mb-4 text-sm text-gray-700">
              <p className="mb-2">Based on the case details, what is your assessment? Include:</p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                <li>Your differential diagnosis</li>
                <li>Diagnostic tests you would order</li>
                <li>Initial treatment plan</li>
              </ul>
            </div>

            <div>
              <textarea
                ref={textareaRef}
                value={response}
                maxLength={5000}
                onChange={(e) => {
                  setResponse(e.target.value);
                  if (submitError) setSubmitError('');
                }}
                className={`w-full min-h-[220px] p-4 bg-gray-50/70 border-2 rounded-xl focus:bg-white focus:outline-none resize-none text-sm text-gray-900 leading-relaxed overflow-hidden transition-all ${
                  submitError ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-[#7367f0] focus:ring-2 focus:ring-[#7367f0]/10'
                }`}
                placeholder="Type your clinical approach here..."
              />
              <div className="flex justify-between items-center mt-2 px-1 text-xs text-gray-400">
                <span>{response.length} / 5000 characters</span>
                <span className="italic">Be thorough in your reasoning</span>
              </div>
            </div>
            {submitError && <div className="text-red-500 text-xs mt-2 px-1">{submitError}</div>}
          </div>

          {/* Clinical Thinking Tip */}
          <div className="bg-[#f8f5ff] rounded-xl border border-purple-200 p-5 flex gap-4 items-start">
            <Lightbulb className="text-purple-600 shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-sm font-bold text-purple-900 mb-1">Clinical Thinking Tip</h4>
              <p className="text-sm text-purple-800">
                Consider the patient's age, species-specific conditions, and prioritize life-threatening differentials. Justify your diagnostic choices.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col items-center">
            <button
              onClick={handleSubmit}
              disabled={response.trim().length < 50 || isSubmitting}
              className="w-full bg-[#7367f0] hover:bg-[#5e50ee] text-white py-3.5 rounded-lg font-medium flex justify-center items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Send size={16} /> {isSubmitting ? 'Submitting...' : 'Submit Your Response'}
            </button>
            {response.trim().length < 50 && (
              <p className="text-xs text-gray-500 mt-3 text-center">
                Please provide at least 50 characters for a meaningful response
              </p>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default CaseResponsePage;
